import { useEffect, useRef, useState } from "react";

import { ApiError } from "../api/errors";
import type { ColorVariant, ProductImage } from "../api/types";
import { ConfirmDialog } from "./ConfirmDialog";
import {
  checkFile,
  formatBytes,
  isTerminal,
  type ReplacementStage,
  type ReplacementState,
} from "./useImageReplacement";

const STAGE_LABEL: Record<ReplacementStage, string> = {
  idle: "",
  preparing: "Preparing…",
  uploading: "Uploading…",
  queued: "Queued",
  processing: "Processing",
  needs_review: "Review needed",
  completed: "Completed",
  failed: "Failed",
};

/**
 * One original view of one colour: Front, Side or Angle.
 *
 * The preview shown is the generated gallery image for that view, because the
 * raw source is never served to the browser. It is labelled as the generated
 * image so nobody mistakes it for the file on disk.
 */
export function SourceViewControl({
  variant,
  label,
  imageRole,
  preview,
  regenerates,
  editable,
  active,
  state,
  onReplace,
  onDismiss,
}: {
  variant: ColorVariant;
  label: string;
  imageRole: string;
  preview: ProductImage | undefined;
  regenerates: string;
  editable: boolean;
  /** True when this control owns the replacement currently in flight. */
  active: boolean;
  state: ReplacementState;
  onReplace: (file: File, imageRole: string) => void;
  onDismiss: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  // Object URLs are a manual resource: one revoke per create, including when
  // the component goes away mid-replacement.
  useEffect(() => {
    if (!file) {
      setLocalPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setLocalPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const busy = active && !isTerminal(state.stage) && state.stage !== "idle";

  function choose(next: File | null) {
    setLocalError(null);
    if (!next) {
      setFile(null);
      return;
    }
    const problem = checkFile(next);
    if (problem) {
      setLocalError(problem);
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    setFile(next);
  }

  function clear() {
    setFile(null);
    setLocalError(null);
    setConfirming(false);
    if (inputRef.current) inputRef.current.value = "";
  }

  const inputId = `replace-${variant.id}-${imageRole}`;

  return (
    <div className="source-view">
      <div className="source-view__head">
        <strong>{label}</strong>
        {active && state.stage !== "idle" ? (
          <span className={`badge badge--${stageBadge(state.stage)}`}>
            {STAGE_LABEL[state.stage]}
          </span>
        ) : null}
      </div>

      <div className="source-view__preview">
        {localPreview ? (
          <>
            <img src={localPreview} alt={`Selected replacement for ${label}`} />
            <span className="source-view__caption">Selected file</span>
          </>
        ) : preview ? (
          <>
            <img
              src={preview.public_url}
              alt={`Generated ${label.toLowerCase()} image for ${variant.name}`}
              loading="lazy"
            />
            <span className="source-view__caption muted">Generated image</span>
          </>
        ) : (
          <div className="source-view__empty muted">No generated image yet</div>
        )}
      </div>

      {file ? (
        <p className="source-view__file mono">
          {file.name} · {formatBytes(file.size)}
        </p>
      ) : null}

      {localError ? <p className="field-error">{localError}</p> : null}

      {active && state.stage === "failed" && state.error ? (
        <ReplacementError error={state.error} />
      ) : null}

      {active && state.stage === "needs_review" ? (
        <div className="notice notice--warning">
          The new images were produced but flagged for review.
          {state.warnings.length ? (
            <ul>
              {state.warnings.slice(0, 4).map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      {active && state.stage === "completed" ? (
        <div className="notice notice--success">New images generated.</div>
      ) : null}

      {editable ? (
        <div className="source-view__actions">
          <label className="sr-only" htmlFor={inputId}>
            Replace {label} for {variant.name}
          </label>
          <input
            id={inputId}
            ref={inputRef}
            type="file"
            accept="image/avif,image/webp,image/png,image/jpeg"
            disabled={busy}
            onChange={(e) => choose(e.target.files?.[0] ?? null)}
          />
          <div className="row">
            <button
              type="button"
              className="btn btn--sm btn--primary"
              disabled={!file || busy}
              onClick={() => setConfirming(true)}
            >
              Replace {label}
            </button>
            {file || (active && isTerminal(state.stage)) ? (
              <button
                type="button"
                className="btn btn--sm"
                disabled={busy}
                onClick={() => {
                  clear();
                  onDismiss();
                }}
              >
                Cancel
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {confirming && file ? (
        <ConfirmDialog
          title={`Replace the ${label.toLowerCase()} view of ${variant.name}?`}
          confirmLabel={`Replace ${label}`}
          busy={busy}
          onCancel={() => setConfirming(false)}
          onConfirm={() => {
            setConfirming(false);
            onReplace(file, imageRole);
          }}
        >
          <p>
            This uploads a new original {label.toLowerCase()} photograph. The
            product is then rebuilt from its complete set of originals,
            regenerating {regenerates} as a new version — not one file
            overwritten.
          </p>
          <p>
            The published revision keeps its current images and stays visible to
            customers until you publish these changes.
          </p>
        </ConfirmDialog>
      ) : null}
    </div>
  );
}

function stageBadge(stage: ReplacementStage): string {
  if (stage === "completed") return "published";
  if (stage === "failed") return "archived";
  if (stage === "needs_review") return "needs_review";
  return "draft";
}

/** The signed URL is never part of this: the error carries no URL at all. */
function ReplacementError({ error }: { error: unknown }) {
  if (error instanceof ApiError && error.code === "source_set_incomplete") {
    const missing = error.detail?.missing_roles ?? [];
    return (
      <div className="notice notice--error" role="alert">
        <strong>The original photographs for this colour are incomplete.</strong>
        <p>
          Replacing one view rebuilds the colour from all of its originals, and{" "}
          {missing.length
            ? `the ${missing.join(" and ")} view${missing.length > 1 ? "s are" : " is"} missing.`
            : "at least one view is missing."}{" "}
          The generated images cannot stand in for a missing original, so this
          colour has to be re-uploaded in full.
        </p>
      </div>
    );
  }
  return (
    <div className="notice notice--error" role="alert">
      {error instanceof ApiError ? error.userMessage : "The replacement failed."}
      {error instanceof ApiError && error.issues.length ? (
        <ul>
          {error.issues.map((issue, index) => (
            <li key={`${issue.code}-${index}`}>{issue.message}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
